// SkimVerificationPanel — shared verification flag review component
// Used in: SkimUploadPage (upload flow) and SkimHistoryPage (job-detail view)
// Renders only when verifyEnabled=true (REACT_APP_SKIM_VERIFY_ENABLED flag).

import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Container,
  Header,
  Input,
  SpaceBetween,
} from '@awsui/components-react';
import { skimApi } from './skimApi';

const SkimVerificationPanel = ({
  jobId,
  sections = [],
  onSectionsChange,
  onComplete,
  completeLabel = 'Continue to Tag Review',
  verifyEnabled = false,
  hideCompleteButton = false,
}) => {
  const [flagOverrideInputs, setFlagOverrideInputs] = useState({});
  const [showOverrideInput, setShowOverrideInput] = useState({});
  const [error, setError] = useState(null);

  if (!verifyEnabled) return null;

  const allResolved = sections.every(
    (s) => s.overridden || s.applied || s.fix_myself,
  );

  const updateSection = (idx, patch) => {
    const updated = sections.map((s, i) => (i === idx ? { ...s, ...patch } : s));
    if (onSectionsChange) onSectionsChange(updated);
  };

  const handleApply = async (section, idx) => {
    try {
      const result = await skimApi.submitVerifyResolution(
        jobId,
        section.section_index ?? idx,
        'apply',
      );
      updateSection(idx, {
        applied: !result.apply_partial,
        apply_partial: result.apply_partial,
        correction_note: result.correction_note,
      });
    } catch (e) {
      setError(e.message);
    }
  };

  const handleOverride = async (section, idx) => {
    const justification = (flagOverrideInputs[idx] || '').trim();
    if (!justification) return;
    try {
      await skimApi.submitVerifyResolution(
        jobId,
        section.section_index ?? idx,
        'override',
        { justification },
      );
      updateSection(idx, { overridden: true });
      setShowOverrideInput((prev) => ({ ...prev, [idx]: false }));
    } catch (e) {
      setError(e.message);
    }
  };

  const handleFixMyself = async (section, idx) => {
    try {
      await skimApi.submitVerifyResolution(
        jobId,
        section.section_index ?? idx,
        'fix-myself',
      );
      updateSection(idx, { fix_myself: true });
    } catch (e) {
      setError(e.message);
    }
  };

  return (
    <Container
      header={
        <Header
          variant="h2"
          description={
            allResolved
              ? 'All flags resolved — ready to proceed.'
              : `${sections.filter((s) => !s.overridden && !s.applied && !s.fix_myself).length} flag(s) require your attention.`
          }
        >
          Verification Results
        </Header>
      }
    >
      <SpaceBetween direction="vertical" size="m">
        {error && (
          <Alert type="error" dismissible onDismiss={() => setError(null)}>
            {error}
          </Alert>
        )}

        {sections.map((section, idx) => {
          const resolved = section.overridden || section.applied || section.fix_myself;
          const isApplyPartial = section.apply_partial && section.correction_note;

          return (
            <div
              key={idx}
              style={{
                border: `1px solid ${resolved ? '#1d8102' : '#d13212'}`,
                borderLeft: `4px solid ${resolved ? '#1d8102' : '#d13212'}`,
                borderRadius: '4px',
                padding: '16px',
                opacity: resolved ? 0.75 : 1,
              }}
            >
              <SpaceBetween direction="vertical" size="s">
                <Box
                  color={resolved ? 'text-status-success' : 'text-status-error'}
                  fontWeight="bold"
                  fontSize="body-s"
                >
                  {resolved
                    ? '✓ Resolved'
                    : '⚠ Flag: ' + (section.reason || 'Flagged for review')}
                </Box>

                <div
                  style={{
                    background: resolved ? '#f2fcf3' : '#fdf3f0',
                    padding: '10px',
                    borderRadius: '4px',
                    fontFamily: 'monospace',
                    fontSize: '13px',
                  }}
                >
                  "{section.content || ''}"
                </div>

                {section.confidence && (
                  <Box color="text-body-secondary" fontSize="body-s">
                    Confidence: {Math.round(section.confidence * 100)}%
                  </Box>
                )}

                {!resolved && section.suggestion && !isApplyPartial && (
                  <div
                    style={{
                      background: '#f2f3f3',
                      padding: '8px',
                      borderRadius: '4px',
                    }}
                  >
                    <Box color="text-body-secondary" fontSize="body-s">
                      <strong>Suggested correction:</strong> {section.suggestion}
                    </Box>
                  </div>
                )}

                {isApplyPartial && (
                  <Alert type="warning">
                    Could not apply automatically — text not found verbatim. Manual
                    correction needed: <em>{section.correction_note}</em>
                  </Alert>
                )}

                {!resolved && (
                  <SpaceBetween direction="vertical" size="xs">
                    <SpaceBetween direction="horizontal" size="xs">
                      {section.suggestion && !isApplyPartial && (
                        <Button variant="normal" onClick={() => handleApply(section, idx)}>
                          Apply correction
                        </Button>
                      )}
                      <Button
                        variant="normal"
                        onClick={() =>
                          setShowOverrideInput((prev) => ({
                            ...prev,
                            [idx]: !prev[idx],
                          }))
                        }
                      >
                        Override with reason
                      </Button>
                      <Button variant="normal" onClick={() => handleFixMyself(section, idx)}>
                        I'll fix this myself
                      </Button>
                    </SpaceBetween>

                    {showOverrideInput[idx] && (
                      <SpaceBetween direction="horizontal" size="xs">
                        <Input
                          value={flagOverrideInputs[idx] || ''}
                          onChange={({ detail }) =>
                            setFlagOverrideInputs((prev) => ({
                              ...prev,
                              [idx]: detail.value,
                            }))
                          }
                          placeholder="Why is this correct despite the flag? (required)"
                        />
                        <Button
                          variant="primary"
                          disabled={!(flagOverrideInputs[idx] || '').trim()}
                          onClick={() => handleOverride(section, idx)}
                        >
                          Submit
                        </Button>
                        <Button
                          onClick={() =>
                            setShowOverrideInput((prev) => ({
                              ...prev,
                              [idx]: false,
                            }))
                          }
                        >
                          Cancel
                        </Button>
                      </SpaceBetween>
                    )}
                  </SpaceBetween>
                )}
              </SpaceBetween>
            </div>
          );
        })}

        {!hideCompleteButton && (
          <Box float="right">
            <Button
              variant="primary"
              disabled={!allResolved}
              onClick={onComplete}
            >
              {completeLabel}
            </Button>
          </Box>
        )}
      </SpaceBetween>
    </Container>
  );
};

export default SkimVerificationPanel;
